import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "@/lib/push";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export const ProfileDialog = ({ open, onOpenChange }) => {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", notify_email: true });
  const [saving, setSaving] = useState(false);
  const [pushOn, setPushOn] = useState(false);

  useEffect(() => {
    if (user && open) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        notify_email: user.notify_email ?? true,
      });
      if (pushSupported()) isPushEnabled().then(setPushOn).catch(() => {});
    }
  }, [user, open]);

  const togglePush = async (v) => {
    try {
      if (v) {
        await enablePush();
        setPushOn(true);
        toast.success(t("saved"));
      } else {
        await disablePush();
        setPushOn(false);
      }
    } catch (e) {
      setPushOn(false);
      toast.error(e.message === "permission_denied" ? t("push_denied") : apiError(e));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me", form);
      setUser(data);
      toast.success(t("saved"));
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("my_profile")}</DialogTitle>
          <DialogDescription className="sr-only">{t("my_profile")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("name")}</Label>
            <Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("phone_number")}</Label>
            <Input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+33612345678" />
            <p className="text-xs text-muted-foreground">{t("phone_hint")}</p>
          </div>
          <div className="flex items-center justify-between border border-border rounded-lg px-4 py-3">
            <Label className="cursor-pointer">{t("notify_email_label")}</Label>
            <Switch data-testid="profile-notify-email" checked={form.notify_email} onCheckedChange={(v) => setForm((f) => ({ ...f, notify_email: v }))} />
          </div>
          {pushSupported() && (
            <div className="flex items-center justify-between border border-accent/30 bg-accent/5 rounded-lg px-4 py-3">
              <Label className="cursor-pointer">{t("push_notifications")}</Label>
              <Switch data-testid="profile-notify-push" checked={pushOn} onCheckedChange={togglePush} />
            </div>
          )}
          <Button data-testid="profile-save-btn" onClick={save} disabled={saving} className="w-full rounded-full active:scale-95">
            {saving ? "..." : t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
